import { createSwiper } from '@nulo/swiper';

/**
 * @param {string | undefined} value
 * @returns {number | null}
 */
function parseDestroyAbove(value) {
  const parsed = Number.parseInt(value ?? '', 10);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  return parsed;
}

/**
 * @extends {HTMLElement}
 */
class ProductRelatedProducts extends HTMLElement {
  /** @type {import('swiper').Swiper | null} */
  #swiper = null;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {IntersectionObserver | null} */
  #io = null;

  /** @type {MediaQueryList | null} */
  #desktopMq = null;

  /** @type {(() => void) | null} */
  #mqListener = null;

  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;

    const destroyAbove = parseDestroyAbove(this.dataset.destroyAbove);
    if (destroyAbove) {
      this.#desktopMq = window.matchMedia(`(min-width: ${destroyAbove}px)`);
      this.#mqListener = () => this.#syncSwiper();
      this.#desktopMq.addEventListener('change', this.#mqListener);
      this.#syncSwiper();
      return;
    }

    this.#observeAndInit();
  }

  disconnectedCallback() {
    this.#teardownObserver();
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    this.#destroySwiper();

    if (this.#mqListener && this.#desktopMq) {
      this.#desktopMq.removeEventListener('change', this.#mqListener);
    }
    this.#mqListener = null;
    this.#desktopMq = null;
    this._initialized = false;
  }

  reinit() {
    this.disconnectedCallback();
    this.connectedCallback();
  }

  #syncSwiper() {
    if (this.#desktopMq?.matches) {
      this.#teardownObserver();
      this.#destroySwiper();
      return;
    }

    this.#observeAndInit();
  }

  #observeAndInit() {
    if (this.#swiper) return;

    if (typeof IntersectionObserver !== 'function') {
      this.#initSwiper();
      return;
    }

    if (this.#io) return;

    this.#io = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.#teardownObserver();
        this.#initSwiper();
      },
      { rootMargin: '200px 0px' }
    );
    this.#io.observe(this);
  }

  #teardownObserver() {
    this.#io?.disconnect();
    this.#io = null;
  }

  #initSwiper() {
    if (this.#swiper) return;

    const carousel = this.querySelector('[data-nulo-carousel]');
    const swiperEl = carousel?.querySelector('[data-swiper]');
    if (!(carousel instanceof HTMLElement) || !(swiperEl instanceof HTMLElement)) return;

    const slideCount = Number.parseInt(this.dataset.slideCount || '0', 10);
    const prevEl = carousel.querySelector('[data-related-prev]');
    const nextEl = carousel.querySelector('[data-related-next]');
    const showNavigation = slideCount > 1 && prevEl && nextEl;

    /** @type {Record<string, unknown>} */
    const config = {
      slidesPerView: 1.5,
      spaceBetween: 20,
      speed: 500,
      watchOverflow: true,
      watchSlidesProgress: true,
      observer: true,
      observeParents: true,
      breakpoints: {
        480: {
          slidesPerView: 1.4,
          spaceBetween: 20,
        },
        768: {
          slidesPerView: 1.8,
          spaceBetween: 20,
        },
        992: {
          slidesPerView: 3,
          spaceBetween: 20,
        },
        1200: {
          slidesPerView: 4,
          spaceBetween: 20,
        },
      },
    };

    if (showNavigation) {
      config.navigation = { nextEl, prevEl };
    }

    this.#swiper = createSwiper({
      region: carousel,
      el: swiperEl,
      activeSlideClass: 'swiper-slide-fully-visible',
      config,
    });

    this.#watchVisibility(carousel);
  }

  #destroySwiper() {
    this.#swiper?.destroy?.(true, true);
    this.#swiper = null;
  }

  /**
   * Swiper cannot measure slide widths while the carousel is display:none.
   * Recompute once it becomes visible.
   * @param {HTMLElement} carousel
   */
  #watchVisibility(carousel) {
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;

    if (typeof ResizeObserver === 'undefined') return;

    this.#resizeObserver = new ResizeObserver(() => {
      if (carousel.clientWidth > 0) this.#swiper?.update();
    });
    this.#resizeObserver.observe(carousel);
  }
}

if (!customElements.get('product-related-products')) {
  customElements.define('product-related-products', ProductRelatedProducts);
}
